/**
 * Citation processing utilities for OpenAI web search responses.
 * 
 * OpenAI's search models return annotations with url_citation objects
 * that mark character ranges in the text as citations to web sources.
 */

export interface UrlCitation {
  type: "url_citation";
  url: string;
  title: string;
  start_index: number;
  end_index: number;
}

export interface CitationSource {
  index: number;
  url: string;
  title: string;
  hostname: string;
}

export interface ProcessedMessage {
  text: string;
  sources: CitationSource[];
}

/**
 * Extract hostname from a URL for display purposes.
 */
function getHostname(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

/**
 * Normalize annotations from OpenAI's response format.
 * Handles both nested and flat annotation structures.
 */
export function normalizeAnnotations(raw: any[]): UrlCitation[] {
  if (!raw || !Array.isArray(raw)) return [];

  return raw
    .filter((a) => a?.type === "url_citation")
    .map((a) => {
      // Handle nested format: { type: "url_citation", url_citation: { url, title, ... } }
      const citation = a.url_citation || a;
      return {
        type: "url_citation" as const,
        url: citation.url || "",
        title: citation.title || "",
        start_index: citation.start_index ?? 0,
        end_index: citation.end_index ?? 0,
      };
    })
    .filter((a) => a.url); // Discard entries without a URL
}

/**
 * Process a message's text and annotations to produce:
 * 1. Text with citation spans replaced by superscript markers like [1]
 * 2. A deduplicated list of citation sources
 */
export function processCitations(
  text: string,
  annotations: UrlCitation[]
): ProcessedMessage {
  if (!annotations || annotations.length === 0) {
    return { text, sources: [] };
  }

  // Deduplicate by URL, assign citation numbers
  const urlToIndex = new Map<string, number>();
  const sources: CitationSource[] = [];

  // Sort by start_index to assign citation numbers in reading order
  const sorted = [...annotations].sort((a, b) => a.start_index - b.start_index);

  for (const ann of sorted) {
    if (!urlToIndex.has(ann.url)) {
      const idx = sources.length + 1;
      urlToIndex.set(ann.url, idx);
      sources.push({
        index: idx,
        url: ann.url,
        title: ann.title,
        hostname: getHostname(ann.url),
      });
    }
  }

  // Replace annotated text spans from end to start (to preserve indices)
  let processed = text;
  const reverseSorted = [...sorted].reverse();

  for (const ann of reverseSorted) {
    const citNum = urlToIndex.get(ann.url)!;
    const before = processed.slice(0, ann.start_index);
    const after = processed.slice(ann.end_index);
    // Use a special link format that CitationBadge can detect:
    // [^n^](url) where n is the citation number
    processed = `${before}[^${citNum}^](${ann.url})${after}`;
  }

  return { text: processed, sources };
}

/**
 * Check if a markdown link is a citation badge link.
 * Citation links use the format [^n^](url)
 */
export function isCitationLink(children: React.ReactNode): number | null {
  if (!children) return null;
  const text = String(children);
  const match = text.match(/^\^(\d+)\^$/);
  return match ? parseInt(match[1], 10) : null;
}
